import { useCallback, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetSchemas from "datasets/hooks/useDatasetSchemas";
import { usePreviewMode } from "datasets/hooks/usePreviewMode";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { Archive, Check } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { DatasetAppearanceSection } from "~/pages/manifold-nextgen/components/datasets/DatasetAppearanceSection";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { ROUTE_NEXTGEN_DATASETS } from "~/router/constants";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

export const DatasetSettings = () => {
  const dataset = useOutletContext<Dataset>();
  const { mutate } = useDataset();
  const { data: schemas } = useDatasetSchemas();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const canEdit = useDatasetPermission("edit:catalog-dataset");
  const canDelete = useDatasetPermission("delete:catalog-dataset") && canEdit;
  const previewFeatureEnabled = usePreviewMode();
  const isOwner = dataset.access_level === DatasetAccessLevel.OWNER;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEnablingPreview, setIsEnablingPreview] = useState(false);

  const handleCopy = useCallback(
    async (text: string, label: string) => {
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} copied`);
    },
    [showSuccess]
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        is_deleted: true,
      });
      showSuccess(`"${dataset.title}" archived`);
      navigate(ROUTE_NEXTGEN_DATASETS, { replace: true });
    } catch {
      showError("Failed to archive dataset");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleEnablePreview = async () => {
    setIsEnablingPreview(true);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/preview/enable`,
        "POST",
        undefined,
        { expectsText: true }
      );
      mutate((current) => (current ? { ...current, previewEnabled: true } : current), {
        revalidate: true,
      });
      showSuccess("Preview mode enabled");
      setPreviewOpen(false);
    } catch {
      showError("Unable to enable preview mode");
    } finally {
      setIsEnablingPreview(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <SettingsSection title="General">
        <SettingsRow title="Dataset ID" value={dataset.id}>
          <Button variant="outline" className="w-32" onClick={() => handleCopy(dataset.id, "ID")}>
            Copy
          </Button>
        </SettingsRow>
        {dataset.slug && (
          <SettingsRow title="Slug" value={dataset.slug}>
            <Button
              variant="outline"
              className="w-32"
              onClick={() => handleCopy(dataset.slug!, "Slug")}
            >
              Copy
            </Button>
          </SettingsRow>
        )}
        {schemas?.items.map((schema) => (
          <SettingsRow
            key={schema.name}
            title="Schema"
            value={
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{schema.name}</span>
                </TooltipTrigger>
                <TooltipContent>{schema.description}</TooltipContent>
              </Tooltip>
            }
          >
            <Button
              variant="outline"
              className="w-32"
              onClick={() => handleCopy(schema.name, "Schema")}
            >
              Copy
            </Button>
          </SettingsRow>
        ))}
        {schemas && schemas.metadata.has_more && (
          <SettingsRow
            title="Schemas"
            value={`+${schemas.metadata.total_count - schemas.items.length} more`}
          />
        )}
        {previewFeatureEnabled && isOwner && (
          <SettingsRow
            title="Preview mode"
            description="Allow sharing this dataset with preview access, where users see metadata only."
          >
            {dataset.previewEnabled ? (
              <Button variant="outline" className="w-32" disabled>
                <Check className="size-4" />
                Enabled
              </Button>
            ) : (
              <Button variant="outline" className="w-32" onClick={() => setPreviewOpen(true)}>
                Enable…
              </Button>
            )}
          </SettingsRow>
        )}
      </SettingsSection>

      {isOwner && <DatasetAppearanceSection dataset={dataset} />}

      {canDelete && (
        <SettingsSection title="Danger Zone" danger>
          <SettingsRow
            title="Archive dataset"
            description="Archive and hide this dataset. An admin can restore if necessary."
          >
            <Button variant="destructive" className="w-32" onClick={() => setDeleteOpen(true)}>
              <Archive className="size-4" />
              Archive
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive &ldquo;{dataset.title}&rdquo;</AlertDialogTitle>
            <AlertDialogDescription>
              Archive and hide this dataset. An admin can restore if necessary.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Archiving…" : "Archive"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Preview Mode</AlertDialogTitle>
            <AlertDialogDescription>
              You are enabling preview mode for &ldquo;{dataset.title}&rdquo;. This allows the
              dataset to be shared with preview access, where users see metadata only, not
              underlying data. This setting cannot be disabled without administrator assistance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnablingPreview}>Cancel</AlertDialogCancel>
            <Button onClick={handleEnablePreview} disabled={isEnablingPreview}>
              {isEnablingPreview ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
